import { Fragment, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Carousel from "react-multi-carousel";
import { T, TF } from "react-translator-component";
import appServices from "../../../services/app-services/appServices";
import { pushMemberGroupComment, pushUserComment } from "../../../services/gtm";
import { convertHeicToJpeg, getFileExtenction } from "../../../common/files";
import { imageAcceptedExtension, validateImage } from "../../../common/images";
import { isAndroid, isClosedGroup, isRegularAppUser } from "../../../util/utils";
import { PostImageItem as ImageItem } from "../../../util/types";
import NameOptionModal from "../NameOptionModal";
import CommentDetail from "./CommentDetail";
import NoticeBanner from "../../NoticeBanner";
import LoadingModal from "../../LoadingModal";
import { ErrorMessageModal } from "../../ErrorMessageModal";
import DeleteIcon from "@mui/icons-material/Delete";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import SendIcon from "@mui/icons-material/Send";
import {
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { colors } from "../../../common/colors";

interface Props {
  comments: any[];
  post: any;
  closeModal(): void;
  onChange: (comments: any[], count: number) => void;
}

const MAX_NUMBER_IMAGE_COUNT = 5;

const CommentsModel = ({ comments, post, closeModal, onChange }: Props) => {
  const responsive = {
    superLargeDesktop: {
      // the naming can be any, depends on you.
      breakpoint: { max: 4000, min: 3000 },
      items: 5,
    },
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 3,
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 2,
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 2,
    },
  };

  const { user } = useSelector((state: any) => state.userAuth);
  const [canModify, setCanModify] = useState<boolean>(false);
  const [commentText, setCommentText] = useState<string>("");
  const [editCommentId, setEditCommentId] = useState<number | null>(null);
  const [replyCommentId, setReplyCommentId] = useState<number | null>(null);
  const [childComment, setChildComment] = useState<string>("");
  const [images, setImage] = useState<any>([]);
  const fileInputRef = useRef<any>();

  const [loading, setLoading] = useState<boolean>(false);
  const [postComments, setPostComments] = useState<any[]>([]);
  const [showNameOptionModal, setShowNameOptionModal] = useState<boolean>(false);

  const [notice, setNotice] = useState<string>("");
  const [noticeType, setNoticeType] = useState<"error" | "success">("error");
  const [displayNotice, setDisplayNotice] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>("");

  useEffect(() => {
    setCanModify(!isClosedGroup(post.member_group?.closed_at))
    getPostComments()
  }, []);

  const deleteItem = (index: number) => {
    setImage((prev: ImageItem[]) => prev.filter((_: ImageItem, i: number) => i !== index));
  };

  const handleIconClick = () => {
    setNotice(TF(T("error_image_count"), MAX_NUMBER_IMAGE_COUNT));
    setNoticeType("error")
    setDisplayNotice(true)
    if (isAndroid(user.client_id)) {
      if (images.length < 5) {
        fileInputRef.current.click();
      } else {
        setNotice(TF(T("error_image_count"), MAX_NUMBER_IMAGE_COUNT));
        setNoticeType("error")
        setDisplayNotice(true)
      }
    } else {
      fileInputRef.current.click();
    }
  };

  const editComment = async (nameOptionValue: string) => {
    if (!editCommentId) return false

    const comment = await appServices.editComment(
      editCommentId,
      childComment,
      post.id,
      nameOptionValue
    );
    displayNgWordNotice(comment.comment.status)
    setEditCommentId(null);
    setReplyCommentId(null);

    return true
  };

  const replyComment = async (nameOptionValue: string) => {
    if (!replyCommentId) return false

    const comment = await appServices.replyComment(
      childComment,
      post.id,
      replyCommentId,
      nameOptionValue
    );
    displayNgWordNotice(comment.comment.status)
    setEditCommentId(null);
    setReplyCommentId(null);
    if (post.member_group) {
      pushMemberGroupComment(user, post);
    } else {
      pushUserComment(user, post);
    }

    return true
  };

  const createNewComment = async (nameOptionValue: string) => {
    if (!commentText && images.length === 0) return false

    const comment = await appServices.createComment(
      commentText,
      post.id,
      nameOptionValue,
      images
    );
    displayNgWordNotice(comment.comment.status)
    setCommentText("");
    setImage([]);
    if (post.member_group) {
      pushMemberGroupComment(user, post);
    } else {
      pushUserComment(user, post);
    }

    return true
  };

  const submitComment = async (nameOptionValue: string) => {
    let result = false;
    try {
      if (editCommentId) {
        result = await editComment(nameOptionValue);
      } else if (replyCommentId) {
        result = await replyComment(nameOptionValue)
      } else {
        result = await createNewComment(nameOptionValue)
      }
    } catch (error: any) {
      console.error(error)
      handleOpenErrMessageModal(error?.response?.data?.message ?? T("api_unknown_error"))
      return false
    } finally {
      setShowNameOptionModal(false);
    }

    return result
  };

  const handleSubmitEditOrReplay = (text: string, editCommentId: number | null, parentCommentId: number | null) => {
    setEditCommentId(editCommentId)
    setReplyCommentId(parentCommentId)
    setChildComment(text)
    setShowNameOptionModal(true);
  };

  const getPostComments = async () => {
    try {
      const res = await appServices.getPostCommnets(post.id);
      setPostComments(res.post_comments.comments);
      onChange(res.post_comments.comments, res.post_comments.comments_count)
    } catch (error: any) {
      console.error(error)
    }
  };

  const onImageChange = (event: any) => {
    if (!event.target.files) {
      console.error("ファイルを取得できませんでした。", event)
      return
    }

    const selectedFiles: any[] = Array.from(event.target.files);
    if (selectedFiles.length === 0) {
      console.error("ファイルがゼロ件です。");
      return
    }

    // 入力チェック
    let msg = ""
    const totalFiles = images.length + selectedFiles.length;
    if (totalFiles > MAX_NUMBER_IMAGE_COUNT) {
      msg = TF(T("error_image_count"), MAX_NUMBER_IMAGE_COUNT);
      selectedFiles.splice(MAX_NUMBER_IMAGE_COUNT - images.length);
    }

    let errMsg = ""
    for (let file of selectedFiles) {
      errMsg = validateImage(file)
      if (errMsg) {
        break
      }
    }

    if (errMsg) {
      setNotice(errMsg);
      setNoticeType("error")
      setDisplayNotice(true)
    } else {
      if (msg) {
        setNotice(msg);
        setNoticeType("error")
        setDisplayNotice(true)
      }

      // 選択されたファイルをセット
      const newImages = selectedFiles.map((file: any) => {
        if (getFileExtenction(file) === "heic") {
          return new Promise(async (resolve, reject) => {
            convertHeicToJpeg(file)
              .then((f: File) => resolve({
                file: f,
                _destroy: "0",
              }))
              .catch((e: any) => reject(e))
          })
        } else {
          let reader = new FileReader();
          return new Promise((resolve) => {
            reader.onload = (e: any) => {
              resolve({
                file: file,
                _destroy: "0",
              });
            };
            reader.readAsDataURL(file);
          });
        }
      });

      Promise.all(newImages).then((loadedImages) => {
        setImage((prevImages: any) => [...prevImages, ...loadedImages]);
      }).catch((e: any) => console.error(e));
    }
  };

  const displayNgWordNotice = (status: string) => {
    if (status === 'Approved') return

    setNoticeType("success")
    setNotice(T("include_ng_words"))
    setDisplayNotice(true)
  }

  const handleSelectNameOption = async (value: string) => {
    setLoading(true)
    const result = await submitComment(value);
    if (result) await getPostComments()
    setLoading(false)
  }

  const handleOpenErrMessageModal = (error: string) => {
    if (!error) return

    setShowErrorModal(true)
    setErrorMessage(error)
  }

  const handleCloseErrMessageModal = () => {
    setShowErrorModal(false)
    setErrorMessage("")
  }

  return (
    <div style={styles.container} key={post.id}>
      <LoadingModal visible={loading} />

      <div style={styles.closeBtn}>
        <button style={styles.closeBtnContainer} onClick={() => closeModal()} >
          <Typography
            sx={styles.closeText}
            variant="body2"
            color="textPrimary"
          >
            {T("close")}
          </Typography>
          <KeyboardArrowDownIcon />
        </button>
      </div>

      <div style={styles.modalTitle}>
        <Typography style={styles.title}>{T("comment")}</Typography>
        <Typography style={styles.description}>{T("comment_notice")}</Typography>
      </div>

      <ul style={styles.commentArea}>
        {postComments.map((comment: any) => (
          <Fragment key={`comment_${comment.id}`}>
            <div>
              <CommentDetail
                currentUser={user}
                comment={comment}
                canModify={canModify}
                onContinue={handleSubmitEditOrReplay}
              />
            </div>

            {comment.children.length > 0 &&
              comment.children.map((childComment: any) => (
                <div key={childComment.id} style={styles.childComment}>
                  <CommentDetail
                    currentUser={user}
                    comment={childComment}
                    canModify={canModify}
                    onContinue={handleSubmitEditOrReplay}
                  />

                  {childComment.children.length > 0 &&
                    childComment.children.map((grandchildComment: any) => (
                      <div key={grandchildComment.id} style={styles.grandchildComment}>
                        <CommentDetail
                          currentUser={user}
                          comment={grandchildComment}
                          canModify={canModify}
                          onContinue={handleSubmitEditOrReplay}
                          isReplyable={false}
                        />
                      </div>
                    ))
                  }
                </div>
              ))
            }
          </Fragment>
        ))}
      </ul>

      <div style={styles.formArea}>
        {images.length > 0 && (
          <div style={{ minHeight: 54 }}>
            <Carousel responsive={responsive}>
              {images.map((item: ImageItem, index: number) => (
                <div key={`new_image_${index}`} style={styles.addImage}>
                  <img
                    style={styles.newImage}
                    src={item.file ? URL.createObjectURL(item.file) : ''}
                    alt=""
                  />
                  <DeleteIcon
                    style={styles.deleteIcon}
                    onClick={() => deleteItem(index)}
                    color={"warning"}
                  />
                </div>
              ))}
            </Carousel>
          </div>
        )}

        <div style={styles.formContainer}>
          {canModify && isRegularAppUser(user) && (
            <Stack
              direction={{
                xs: "row",
                sm: "row",
              }}
              justifyContent="flex-end"
              style={styles.formBox}
            >
              <IconButton onClick={handleIconClick}>
                <AddPhotoAlternateIcon />
                <input
                  ref={fileInputRef}
                  accept={imageAcceptedExtension}
                  type="file"
                  hidden
                  multiple
                  onChange={onImageChange}
                />
              </IconButton>

              <TextField
                fullWidth
                id="filled-multiline-static"
                hiddenLabel
                inputProps={{ maxLength: 200 }}
                multiline
                minRows={1}
                maxRows={3}
                onChange={(ev) => setCommentText(ev.target.value)}
                value={commentText}
                variant="filled"
                sx={styles.textField}
              />

              <IconButton
                disabled={commentText.trim() === "" && images.length === 0}
                onClick={() => setShowNameOptionModal(true)}
                aria-label="thumbs"
              >
                <SendIcon />
              </IconButton>
            </Stack>
          )}
        </div>

        <NoticeBanner
          open={displayNotice}
          message={notice}
          type={noticeType}
          onClose={() => setDisplayNotice(false)}
        />
      </div>

      <NameOptionModal
        open={showNameOptionModal}
        onClose={() => setShowNameOptionModal(false)}
        onSelect={(value) => handleSelectNameOption(value)}
      />

      <ErrorMessageModal
        open={showErrorModal}
        onClose={() => handleCloseErrMessageModal()}
        messages={[errorMessage]}
      />
    </div>
  );
};

export default CommentsModel;

const styles = {
  container: {
    height: "100%",
    marginTop: "7px",
    paddingTop: 0,
    borderRadius: "8px",
    background: colors.farm_white,
    overflow: "hidden",
  },

  closeBtnContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    border: 0,
    backgroundColor: colors.farm_white,
  },

  closeBtn: {
    height: "17px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    marginTop: "10px"
  },

  closeText: {
    fontSize: 12,
    color: colors.farm_gray200,
  },

  modalTitle: {
    marginTop: "13px",
    padding: "0 20px"
  },

  title: {
    fontSize: 18,
    lineHeight: "25px",
    fontWeight: "600",
    textAlign: "center" as const,
  },

  description: {
    fontSize: 14,
    color: colors.farm_gray200,
    marginTop: "10px",
  },

  commentArea: {
    height: "calc(100% - 221px)",
    overflow: "auto",
    position: "relative" as const,
    marginTop: "22px",
    padding: "0 20px",
  },

  childComment: {
    marginBottom: 16,
    marginLeft: 40,
  },

  grandchildComment: {
    marginLeft: 18,
    marginTop: 5,
  },

  formArea: {
    position: "absolute" as const,
    bottom: 0,
    left: 0,
    backgroundColor: colors.farm_white,
    width: "100%",
    borderRadius: 4,
  },

  addImage: {
    height: 50,
    width: 150,
    borderRadius: 2,
    borderWidth: 1,
    borderColor: colors.farm_gray100,
  },

  newImage: {
    height: 50,
    width: 150,
    borderRadius: 2
  },

  deleteIcon: {
    top: -30,
    position: "relative" as const
  },

  formContainer: {
    minHeight: "40px",
    backgroundColor: colors.farm_white,
  },

  formBox: {
    backgroundColor: colors.farm_white,
    padding: "12px 0"
  },

  textField: {
    backgroudColor: colors.farm_gray10,
    "& .MuiInputBase-root": {
      minHeight: "40px",
      padding: "5px 12px",
    },
  },
}